import {appendZeros} from 'js/application/components/overview/helpers/deposit';
import {Nullable} from 'js/reusable/types/general';

Number.isFinite = Number.isFinite || function (value) {
	return typeof value === 'number' && isFinite(value);
};

const exported = {
	comparator: function (c1: number, c2: number, direction: 0 | 1) {
		if (direction === undefined) {
			direction = 0;
		}

		if (c1 < c2) {
			return direction ? 1 : -1;
		}

		if (c1 > c2) {
			return direction ? -1 : 1;
		}

		return 0;
	},

	formatLocale: function (
		value: number,
		decimalPlaces: number,
		decimalPoint: string,
		thousandsSep?: string,
		currencyCode?: string,
		type?: string,
		currencyCodeFirst?: boolean,
		round?: boolean
	) {
		thousandsSep  = thousandsSep || '';
		decimalPlaces = decimalPlaces || 0;
		decimalPlaces = type === 'integer' ?  0 : decimalPlaces;

		if (value === undefined) {
			return '-';
		}

		if (type === 'percent') {
			value = value * 100;
		}

		if (type === 'currency') {
			decimalPlaces = this.extendedDecimal(value, decimalPlaces);
		}

		const roundParts =
			type === 'currency'
				? this.currencyRoundParts(value, decimalPlaces, round)
				: this.roundParts(value, decimalPlaces, round);

		let strValue = this.format(roundParts, decimalPoint, thousandsSep);

		if (Number(strValue) === -0) {
			strValue = '0';
		}

		switch (type) {
			case 'currency':
				if (currencyCode === '') {
					return strValue;
				}

				return currencyCodeFirst
					? currencyCode + ' ' + strValue
					: strValue + ' ' + currencyCode;
			case 'percent':
			case 'percentWhole': // percent whole format is used when we do not want to multiply by 100
				return strValue + ' %';
			default:
				return strValue;
		}
	},

	roundParts: function (n: number, numOfDecPlaces: number, round?: boolean) {
		// Round up to nearest integer
		if (round) {
			return [Math.round(n).toString()];
		}

		if (!n) {
			n = 0;
		}

		return this.toFixed(n, numOfDecPlaces).split('.');
	},

	currencyRoundParts: function (n: number, numOfDecPlaces: number, round?: boolean) {
		// Round up to nearest integer
		if (round) {
			return [Math.round(n).toString()];
		}

		if (!n) {
			n = 0;
		}

		const sepPosition = n.toString().indexOf('.');

		const value       =
			sepPosition === -1
				? this.toFixed(n, numOfDecPlaces)
				: n.toString().slice(0, sepPosition + numOfDecPlaces + 1);

		const parts = value.split('.');

		if (parts.length === 1 || parts.length === 2 && parts[1].length < numOfDecPlaces) {
			parts[1] = appendZeros(parts[1] ? parts[1] : '', numOfDecPlaces);
		}

		return parts;
	},

	format: function (parts: string[], decSep: string, groupSep: string) {
		return (
			parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, groupSep) +
			(parts[1] ? decSep + parts[1] : '')
		);
	},

	toFixed: function toFixed(num: number, fixed: number) {
		const re = new RegExp('^-?\\d+(?:\.\\d{0,' + (fixed || -1) + '})?');
		const parts = num.toString().match(re);

		if (parts && parts.length > 0) {
			return parts[0];
		}

		if (fixed === 0) {
			return '0';
		}

		return '0.' + new Array(fixed).fill('0').join('');
	},

	toFixedNumber(num: number, digits: number, base: number = 10) {
		const pow = Math.pow(base, digits);

		return Math.round(num * pow) / pow;
	},

	prepFormatter: function (decPlaces: number, decSep: string) {
		const self = this;

		return function (value: number) {
			return self.formatLocale(value, decPlaces, decSep);
		};
	},

	parseNumber: function (value?: Nullable<string | number>) {
		if (!value) {
			return value === 0 ? 0 : undefined;
		}

		if (typeof value === 'number') {
			return value;
		}

		{if (value.toString) {
			value = value.toString();
		}}

		value = value.trim ? value.trim() : value;

		if (value.match(/([.,])\d([.,])/g)) {
			return undefined;
		}

		if (
			(!isFinite(+value.charAt(0)) && value.charAt(0) !== '-') ||
			(value.length > 1 && !isFinite(+value.charAt(value.length - 1)))
		) {
			return undefined;
		}

		const wholeAndDecimal = value.split(/[.,]/g);

		if (wholeAndDecimal === null) {
			return undefined;
		}

		if (wholeAndDecimal.length === 1) {
			return Number(wholeAndDecimal[0]);
		}

		const decimal = wholeAndDecimal.pop();
		const whole   = wholeAndDecimal;

		return Number(whole.join('') + '.' + decimal);
	},

	extendedDecimal: function (value: number | null, decimalPlaces: number) {
		const decimals = value?.toString().match(/(^0\.0+[1-9])/);

		if (decimals) {
			return decimals[0].length - 2;
		}

		return decimalPlaces;
	},

	/**
	 * Max of two localized float numbers (actual type of string)
	 */
	max: function (n1?: Nullable<string | number>, n2?: Nullable<string | number>) {
		const _n1 = this.parseNumber(n1);
		const _n2 = this.parseNumber(n2);

		if (!_n1 || !_n2) {
			return n2;
		}

		if (_n1 > _n2) {
			return n1;
		}

		return n2;
	},

	isNumeric: function (n: Nullable<string | number>) {
		const num = parseNumber(n);

		if (!num && num !== 0) {
			return false;
		}

		return !isNaN(num) && isFinite(num);
	},

	isNumericOrEmpty: function (n: string) {
		if (n === '') {
			return true;
		}

		return isNumeric(n);
	},

	getDecimalPlaces: function (value: Nullable<string | number> | undefined, minSpaces = 0) {
		value = parseNumber(value);

		if (!value || Math.floor(value) === value) {
			return minSpaces;
		}

		return value.toString().split('.')[1].length || 0;
	},
};

export default exported;

export const {
	comparator,
	formatLocale,
	roundParts,
	currencyRoundParts,
	format,
	prepFormatter,
	parseNumber,
	getDecimalPlaces,
	extendedDecimal,
	max,
	isNumeric,
	isNumericOrEmpty,
	toFixedNumber,
} = exported;
